// Plain-English names for a job in the jobs list, from the few fields the backend keeps for it.
// The list does not say which country a job was for, so none is shown. Pure: no React, router or
// Firebase imports.
import { ALL_ROLES_LABEL, DISTRIBUTIONS, DISTRIBUTION_ROLE_LABELS, GRANULARITY_LABELS, MEASURES, PRODUCT_LABELS, geoTypeNoun } from '../config/products';
import type { DataProductId, DistributionId, DistributionRole, Granularity, MeasureKey } from '../config/products';
import type { Job } from '../services/jobsApi';
import { pluralize } from './plural';
import { formatUtc } from './dateWindow';

type JobFields = Pick<
  Job,
  | 'agg_type'
  | 'measure'
  | 'distribution'
  | 'role'
  | 'activity_type'
  | 'geo_type'
  | 'geo_ids'
  | 'origin_geo_type'
  | 'origin_geo_ids'
  | 'destination_geo_type'
  | 'destination_geo_ids'
  | 'start_time'
  | 'end_time'
>;

const has = (table: object, key: string | null | undefined): boolean =>
  key != null && Object.prototype.hasOwnProperty.call(table, key);

const isProduct = (s: string | null | undefined): s is DataProductId => has(PRODUCT_LABELS, s);
const isMeasure = (s: string | null | undefined): s is MeasureKey => has(MEASURES, s);
const isGranularity = (s: string | null | undefined): s is Granularity => s === 'daily' || s === 'hourly';

type TitleFields = Pick<JobFields, 'agg_type' | 'measure' | 'activity_type' | 'distribution' | 'role'>;

/** A filter as the job doc keeps it (a comma list or an array), one value per entry. */
const filterValues = (v: string | string[] | null | undefined): string[] =>
  (Array.isArray(v) ? v : (v ?? '').split(',')).map((s) => s.trim()).filter(Boolean);

/** 'Where visitors come from', or 'Distributions' for a job of several or none. */
function distributionWords(job: Pick<JobFields, 'distribution'>): string {
  const picked = filterValues(job.distribution);
  return picked.length === 1 && has(DISTRIBUTIONS, picked[0]) ? DISTRIBUTIONS[picked[0] as DistributionId].label : 'Distributions';
}

/** 'Home, Work'; 'All roles' for a job with no role filter. */
function roleWords(job: Pick<JobFields, 'role'>): string {
  const picked = filterValues(job.role);
  if (picked.length === 0) return ALL_ROLES_LABEL;
  return picked.map((r) => (has(DISTRIBUTION_ROLE_LABELS, r) ? DISTRIBUTION_ROLE_LABELS[r as DistributionRole] : r)).join(', ');
}

/** The words a job's title is made of; null for a part the app does not recognise. */
export function jobParts(job: TitleFields): {
  product: string | null;
  measure: string | null;
  resolution: string | null;
} {
  if (job.agg_type === 'activity_distributions') {
    return {
      product: PRODUCT_LABELS.activities,
      measure: `${distributionWords(job)} · ${roleWords(job)}`,
      resolution: isGranularity(job.activity_type) ? GRANULARITY_LABELS[job.activity_type] : null,
    };
  }
  return {
    product: isProduct(job.agg_type) ? PRODUCT_LABELS[job.agg_type] : null,
    measure: isMeasure(job.measure) ? MEASURES[job.measure].label : null,
    resolution: isGranularity(job.activity_type) ? GRANULARITY_LABELS[job.activity_type] : null,
  };
}

/**
 * 'Activities · Unique people · Daily', 'Activities · Where visitors come from · Home · Daily'.
 * A product the app does not know is a "Data job".
 */
export function jobTitle(job: TitleFields): string {
  const { product, measure, resolution } = jobParts(job);
  return [product ?? 'Data job', measure, resolution].filter((p): p is string => p !== null).join(' · ');
}

const countOf = (ids: readonly unknown[] | null | undefined) => (Array.isArray(ids) ? ids.length : 0);

/** '3 municipalities', '2 origin grid cells → 1 destination grid cell', or 'Point lookup' for Flow. */
export function jobAreaSummary(job: JobFields): string {
  if (job.agg_type === 'flow') return 'Point lookup';
  if (job.agg_type === 'odm') {
    const origin = geoTypeNoun(job.origin_geo_type);
    const destination = geoTypeNoun(job.destination_geo_type);
    const o = countOf(job.origin_geo_ids);
    const d = countOf(job.destination_geo_ids);
    return `${pluralize(o, `origin ${origin.singular}`, `origin ${origin.plural}`)} → ${pluralize(d, `destination ${destination.singular}`, `destination ${destination.plural}`)}`;
  }
  if (job.geo_type == null) return 'Point lookup';
  const noun = geoTypeNoun(job.geo_type);
  return pluralize(countOf(job.geo_ids), noun.singular, noun.plural);
}

const instant = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
};

/** '14 Oct 2025, 00:00 to 14 Oct 2025, 23:59 UTC'; '' when either end is missing or unreadable. */
export function jobWindowText(job: Pick<JobFields, 'start_time' | 'end_time'>): string {
  const start = instant(job.start_time);
  const end = instant(job.end_time);
  if (start === null || end === null) return '';
  const bare = (ms: number) => formatUtc(ms, 'hourly').replace(/ UTC$/, '');
  return `${bare(start)} to ${bare(end)} UTC`;
}

/** '14 Oct 2025, 08:00 UTC'; '' when there is no time or it cannot be read. */
export function formatJobTime(iso: string | null | undefined): string {
  const ms = instant(iso);
  return ms === null ? '' : formatUtc(ms, 'hourly');
}

/** 'Created 14 Oct 2025, 08:00 UTC'; '' when the job has no creation time. */
export function jobCreatedText(createdAt: string | null | undefined): string {
  const time = formatJobTime(createdAt);
  return time ? `Created ${time}` : '';
}
